import React from "react";
import { MediaItem } from "../../../../types/MediaItem";

interface MediaLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaItems: MediaItem[];
  selectedImages: MediaItem[];
  onImageSelect: (item: MediaItem) => void;
  loading: boolean;
  /** When true, tap toggles selection; Done confirms (carousel / chat multi-attach). */
  multiSelectMode?: boolean;
  /** Called when user clicks Done (before onClose). */
  onPressDone?: (pickedItem?: MediaItem) => void;
  /** Optional max selection (e.g. Instagram carousel). */
  maxSlots?: number;
  /** Filter visible media by type. */
  mediaTypeFilter?: "image" | "video" | "document" | "all";
  /** Replaces the single-select hint ("Choose one image or video for this post."). */
  subtitle?: string;
  /** Replaces "No media found in your library." when nothing is shown. */
  emptyMessage?: React.ReactNode;
  /** Paged libraries: shows "Load more" while there is more to fetch. */
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  /** The last "Load more" failed; the button stays so the owner can retry. */
  loadMoreFailed?: boolean;
}

export const MediaLibraryModal: React.FC<MediaLibraryModalProps> = ({
  isOpen,
  onClose,
  mediaItems,
  selectedImages,
  onImageSelect,
  loading,
  multiSelectMode = false,
  onPressDone,
  maxSlots,
  mediaTypeFilter = "all",
  subtitle,
  emptyMessage,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  loadMoreFailed = false,
}) => {
  if (!isOpen) return null;

  const loadMore =
    onLoadMore && hasMore ? (
      <div className="mt-4 flex flex-col items-center gap-2">
        {loadMoreFailed && (
          <p role="alert" className="text-[13px] text-theme-fg-muted">
            Couldn't load more just now. Try again.
          </p>
        )}
        <button
          type="button"
          onClick={onLoadMore}
          disabled={loadingMore}
          className="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-fg transition-colors hover:bg-theme-background disabled:cursor-default disabled:opacity-60"
        >
          {loadingMore ? "Loading…" : "Load more"}
        </button>
      </div>
    ) : null;

  const filteredMediaItems = mediaItems.filter((item) => {
    const mediaType = item.media_type.toLowerCase();
    const isDoc = mediaType === "document" || item.s3_key?.toLowerCase().endsWith(".pdf");
    if (mediaTypeFilter === "image") return mediaType === "image" && !isDoc;
    if (mediaTypeFilter === "video") return mediaType === "video" && !isDoc;
    if (mediaTypeFilter === "document") return isDoc;
    return true;
  });

  const hasVideos = filteredMediaItems.some(
    (m) => m.media_type.toLowerCase() === "video"
  );

  const selectionOrder = (item: MediaItem) => {
    const i = selectedImages.findIndex((img) => img.media_id === item.media_id);
    return i >= 0 ? i + 1 : null;
  };

  const handlePick = (item: MediaItem) => {
    if (multiSelectMode && maxSlots != null) {
      const order = selectionOrder(item);
      if (order == null && selectedImages.length >= maxSlots) return;
    }
    onImageSelect(item);
    if (!multiSelectMode) {
      onPressDone?.(item);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-4 flex max-h-[80vh] w-full max-w-4xl flex-col rounded-xl border border-theme-border bg-theme-surface p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-theme-fg">My Library</h3>
            {multiSelectMode && (
              <p className="mt-1 text-[13px] leading-snug text-theme-fg-muted">
                Tap to select multiple items in order. Tap again to deselect.{" "}
                {selectedImages.length > 0 ? "Press Done to confirm." : ""}
                {maxSlots != null && (
                  <span className="block">
                    Up to {maxSlots} items for this carousel.
                  </span>
                )}
              </p>
            )}
            {!multiSelectMode && (
              <p className="mt-1 text-[13px] text-theme-fg-muted">
                {subtitle ?? "Choose one image or video for this post."}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-2 text-theme-fg-muted transition-colors hover:bg-theme-background"
            aria-label="Close"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-theme-primary" />
          </div>
        ) : filteredMediaItems.length === 0 ? (
          <div className="py-12 text-center text-theme-fg-muted">
            {emptyMessage ?? <p>No media found in your library.</p>}
            {loadMore}
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid grid-cols-3 gap-3 md:grid-cols-4 lg:grid-cols-5">
              {filteredMediaItems.map((item) => {
                const isSelected = selectedImages.some(
                  (img) => img.media_id === item.media_id
                );
                const isVideo = item.media_type.toLowerCase() === "video";
                const isDoc = item.media_type.toLowerCase() === "document" || item.s3_key?.toLowerCase().endsWith(".pdf");
                const ord = selectionOrder(item);
                return (
                  <div
                    key={item.media_id}
                    role="button"
                    tabIndex={0}
                    aria-label={item.s3_key?.split("/").pop() || "Media item"}
                    onClick={() => handlePick(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handlePick(item);
                      }
                    }}
                    className={`relative cursor-pointer overflow-hidden rounded-lg border-2 transition-all ${
                      isSelected
                        ? "border-theme-primary ring-2 ring-theme-primary/20"
                        : "border-transparent hover:border-theme-border"
                    }`}
                  >
                    {isDoc ? (
                      <div className="flex h-32 w-full flex-col items-center justify-center bg-surface-ui p-2 text-center">
                        <span className="material-icons-outlined text-3xl text-red-500 mb-1">picture_as_pdf</span>
                        <span className="text-[10px] font-semibold text-theme-fg line-clamp-2 break-all">
                          {item.s3_key.split('/').pop()}
                        </span>
                      </div>
                    ) : isVideo ? (
                      <video
                        src={item.cloudfront_url}
                        muted
                        playsInline
                        className="h-32 w-full object-cover"
                      />
                    ) : (
                      <img
                        src={item.cloudfront_url}
                        alt=""
                        className="h-32 w-full object-cover"
                      />
                    )}
                    {isDoc ? (
                      <span className="absolute bottom-1 right-1 rounded bg-red-600 px-1 text-[10px] font-bold text-white">
                        PDF
                      </span>
                    ) : isVideo ? (
                      <span className="absolute bottom-1 right-1 rounded bg-black/60 px-1 text-[10px] font-bold text-white">
                        VIDEO
                      </span>
                    ) : null}
                    {isSelected && ord != null && (
                      <>
                        <div className="absolute inset-0 bg-theme-primary/15" />
                        <div className="absolute left-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-theme-primary text-[13px] font-bold text-white shadow-md">
                          {ord}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {loadMore}
          </div>
        )}

        {selectedImages.length > 0 && (
          <div className="mt-4 flex items-center justify-between border-t border-theme-border pt-4">
            <span className="text-sm text-theme-fg-muted">
              {selectedImages.length}{" "}
              {selectedImages.length === 1
                ? hasVideos
                  ? "item"
                  : "image"
                : hasVideos
                  ? "items"
                  : "images"}{" "}
              selected
            </span>
            <button
              type="button"
              onClick={() => {
                onPressDone?.();
                onClose();
              }}
              className="rounded-lg bg-theme-primary px-4 py-2 text-white transition-opacity hover:opacity-90"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
